import axios from 'axios';

import { signAPI, userAPI, storyAPI, studioAPI } from 'app/apis';

import { STORY_AD_TYPES } from 'app/utils/constants';

import * as t from './types';

const { API_URL } = process.env;

const socialSignIn = (socialType, accessToken) => ({
  type: t.SOCIAL_SIGN_IN,
  payload: signAPI.socialSignIn(socialType, accessToken),
});

const socialSignUp = (socialType, accessToken) => ({
  type: t.SOCIAL_SIGN_IN,
  payload: signAPI.socialSignUp(socialType, accessToken),
});

const fetchMyInfo = (firebaseIdToken = null, skipError = false) => ({
  type: t.FETCH_MY_INFO,
  payload: userAPI.getMyInfo(),
  meta: { firebaseIdToken },
  skipError,
});

const updateMe = (user) => ({
  type: t.UPDATE_ME,
  meta: { user },
});

const addBalloon =
  (balloon, type = null) =>
  (dispatch, getState) => {
    const {
      user: { isLoaded, data: user },
    } = getState().common;

    if (!isLoaded) {
      return;
    }

    dispatch(
      updateMe({
        balloon: user.balloon + balloon,
        balloonDetail: {
          ...user.balloonDetail,
          ...(type
            ? {
                [type]: user.balloonDetail[type] + balloon,
              }
            : {}),
        },
      })
    );
  };

const changeMyInfo = (data) => ({
  type: t.CHANGE_MY_INFO,
  payload: userAPI.patchUserInfo(data),
});

const unregisterUser = () => ({
  type: t.UNREGISTER_USER,
  payload: userAPI.unregisterUser(),
});

const postMessages = (type, messages, rating = null) => ({
  type: t.POST_MESSAGES,
  payload: userAPI.postMessages(type, messages, rating),
});

const signOut = (deviceType) => ({
  type: t.SIGN_OUT,
  payload: userAPI.signOut(deviceType),
});

const resetUser = (retry = false) => ({
  type: t.RESET_USER,
  retry,
});

const showStorageWarning = () => ({
  type: t.SHOW_STORAGE_WARNING,
});

const hideStorageWarning = () => ({
  type: t.HIDE_STORAGE_WARNING,
});

// ab test
const setExperiments = (data) => ({
  type: t.SET_EXPERIMENTS,
  data,
});

const setShowAdTapCount = (tapCount) => ({
  type: t.SET_SHOW_AD_TAP_COUNT,
  tapCount,
});

const updateABTestDataReceived = (isDataReceived) => ({
  type: t.UPDATE_AB_TEST_DATA_RECEIVED,
  isDataReceived,
});

// adMob
const setAdLoadStatus = (adType, status) => ({
  type: t.SET_AD_LOAD_STATUS,
  adType,
  status,
});

// promotion
const checkPromotions = () => ({
  type: t.CHECK_PROMOTIONS,
  payload: userAPI.checkPromotions(),
});

const setPromotions = (promotions) => ({
  type: t.SET_PROMOTIONS,
  promotions,
});

const fetchFirebaseToken = () => ({
  type: t.FETCH_FIREBASE_TOKEN,
  payload: userAPI.getFirebaseToken(),
});

const fetchUserMarketItems = (userIds) => (dispatch, getState) => {
  const { userMarketItems } = getState().common;
  const newUserIds = userIds.filter((userId) =>
    userMarketItems[userId] ? !userMarketItems[userId].isLoaded : true
  );

  if (newUserIds.length === 0) {
    return Promise.resolve();
  }
  return dispatch({
    type: t.FETCH_USERS_MARKET_ITEMS,
    payload: userAPI.getUsersMarketItems(newUserIds),
    meta: { userIds: newUserIds },
  });
};

const fetchUserFans =
  (id, userIds, force = false) =>
  (dispatch, getState) => {
    const { userFans } = getState().common;

    if (!force) {
      const newUserIds = userIds.filter((userId) => {
        const key = `${userId}:${id}`;
        return userFans[key] ? !userFans[key].isLoaded : true;
      });

      if (newUserIds.length === 0) {
        return Promise.resolve();
      }

      return dispatch({
        type: t.FETCH_USER_FANS,
        payload: userAPI.getUserFans(id, newUserIds),
        skipError: true,
        meta: { userIds: newUserIds, targetUserId: id },
      });
    }

    const notFanUserIds = userIds.filter((userId) => {
      const key = `${userId}:${id}`;
      return userFans[key] ? !userFans[key].data.isFan : true;
    });

    if (notFanUserIds.length === 0) {
      return Promise.resolve();
    }

    return dispatch({
      type: t.FETCH_USER_FANS,
      payload: userAPI.getUserFans(id, notFanUserIds),
      skipError: true,
      meta: { userIds: notFanUserIds, targetUserId: id },
    });
  };

const fetchUserSetting =
  (objectType, objectId, force = false) =>
  (dispatch, getState) => {
    const { userSettings } = getState().common;
    if (!force && userSettings[`${objectType}:${objectId}`]) {
      return Promise.resolve();
    }

    return dispatch({
      type: t.FETCH_USER_SETTING,
      payload: userAPI.getUserSetting(objectType, objectId),
      meta: { objectType, objectId },
      skipError: true,
    });
  };

const setUserSetting = (objectType, objectId, key, value) => ({
  type: t.SET_USER_SETTING,
  payload: userAPI.postUserSetting(objectType, objectId, key, value),
  meta: { objectType, objectId, key },
});

const fetchStoryAd =
  (storyId = null) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      storyAd: { visited },
      latestMyStory: { isLoaded: isLatestMyStoryLoaded },
    } = state.common;
    const {
      location: { pathname, search },
    } = state.router;
    const url = `${pathname}${search}`;

    if (visited.includes(url)) {
      return Promise.resolve({ value: { result: {} } });
    }

    return dispatch({
      type: t.FETCH_STORY_AD,
      payload: storyAPI.getStoryAd(STORY_AD_TYPES.LIST, storyId),
      meta: { url },
      skipError: true,
    }).then(({ value }) => {
      const { result } = value;
      if (!result.id && !isLatestMyStoryLoaded) {
        return dispatch({
          type: t.FETCH_LATEST_MY_STORY,
          payload: studioAPI.getLatestMyStory(),
          skipError: true,
        }).then(() => ({ value }));
      }

      return { value };
    });
  };

const setStoryAdFull = (data) => ({
  type: t.SET_STORY_AD_FULL,
  data,
});

const fetchStoryAdFull =
  (storyId = null) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      storyAdFull: { isLoaded: isStoryAdLoaded, isViewed: isStoryAdViewed },
    } = state.common;
    if (isStoryAdLoaded && !isStoryAdViewed) {
      return Promise.resolve({ value: { result: {} } });
    }

    return dispatch({
      type: t.FETCH_STORY_AD_FULL,
      payload: storyAPI.getStoryAd(STORY_AD_TYPES.FULL, storyId),
      skipError: true,
    })
      .then(({ value }) => {
        const { result } = value;
        if (!result.id) {
          return Promise.reject();
        }

        const {
          id: storyAdId,
          story: { id: _storyId },
          episode: { id: episodeId },
        } = result;

        return axios.get(
          `${API_URL}/stories/${_storyId}/episodes/${episodeId}/`,
          {
            storyAdId,
            storyId: _storyId,
            episodeId,
          }
        );
      })
      .then(({ data, config }) => {
        const {
          content: {
            attachment: { url: contentUrl },
          },
          story: { title: _title },
        } = data.result;

        return axios.get(contentUrl, { ...config, title: _title });
      })
      .then(({ data, config }) => {
        const _data = {
          content: data,
          storyAdId: config.storyAdId,
          storyId: config.storyId,
          episodeId: config.episodeId,
          title: config.title,
          isDefaultAd: false,
        };
        dispatch(setStoryAdFull(_data));

        return _data;
      })
      .catch(() => {
        dispatch(
          setStoryAdFull({
            isDefaultAd: true,
          })
        );
      });
  };

const viewStoryAdFull = () => ({
  type: t.VIEW_STORY_AD_FULL,
});

const resetStoryAdFull = () => ({
  type: t.RESET_STORY_AD_FULL,
});

const clickStoryAd = (storyAdId, type = STORY_AD_TYPES.LIST) => ({
  type: t.CLICK_STORY_AD,
  payload: storyAPI.clickStoryAd(storyAdId, type),
});

const setValue = (key, value) => ({
  type: t.SET_VALUE,
  key,
  value,
});

const setLoading = (isLoading) => ({
  type: t.SET_LOADING,
  isLoading,
});

const fetchVouchers = (voucherType) => ({
  type: t.FETCH_VOUCHERS,
  payload: userAPI.getMyVouchers(voucherType),
  meta: { voucherType },
});

const fetchVoucherRecommendation = (voucherRecommendationType) => ({
  type: t.FETCH_VOUCHER_RECOMMENDATION,
  skipError: true,
  payload: userAPI.getMyVoucherRecommendation(voucherRecommendationType),
});

export default {
  socialSignIn,
  socialSignUp,
  fetchMyInfo,
  updateMe,
  addBalloon,
  changeMyInfo,
  unregisterUser,
  postMessages,
  signOut,
  resetUser,
  showStorageWarning,
  hideStorageWarning,
  setExperiments,
  updateABTestDataReceived,
  setAdLoadStatus,
  checkPromotions,
  setPromotions,
  setShowAdTapCount,
  fetchFirebaseToken,
  fetchUserMarketItems,
  fetchUserFans,
  fetchUserSetting,
  setUserSetting,
  fetchStoryAd,
  fetchStoryAdFull,
  setStoryAdFull,
  viewStoryAdFull,
  resetStoryAdFull,
  clickStoryAd,
  setValue,
  setLoading,
  fetchVouchers,
  fetchVoucherRecommendation,
};



// WEBPACK FOOTER //
// ./src/app/redux/common/actions.js